/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AppWindow,
  ArrowRight,
  ChartColumn,
  CircleHelp,
  Clock,
  History,
  LayoutDashboard,
  Settings,
  UserRound
} from 'lucide-react';
import { supabase } from '../../utilities/supabase';
import { UserProfilePicture } from '../UserProfilePicture';
import { canSeeTeam } from '../dashboard/teamAccess';
import {
  STATES,
  ORDER,
  duration,
  minutesBetween,
  timeOfDay,
  useMyState
} from '../entity/RecordViewers';
import { getRecordPlace, onRecordPlace, pageName } from './onlinePlace';
import SegmentedSwitch from './SegmentedSwitch';

const MAX_SHOWN = 5;
const CHANNEL = 'online:crm';

// ONLINE_TABS_V1: each browser tab is one entry. Where the tab sits:
//  front: the tab they're using (the focused window)
//  open: on screen but another window has focus (a second monitor)
//  back: a background tab
const VIEW = {
  front: { rank: 0, label: 'Active', icon: 'bg-green-50 text-green-600 ring-green-600/10', text: 'text-base-900', status: 'text-green-700', dot: 'bg-green-500' },
  open: { rank: 1, label: 'Open', icon: 'bg-sky-50 text-sky-600 ring-sky-600/10', text: 'text-base-800', status: 'text-base-500', dot: 'bg-sky-400' },
  back: { rank: 2, label: 'Background', icon: 'bg-base-50 text-base-400 ring-base-900/5', text: 'text-base-500', status: 'text-base-400', dot: 'bg-base-300' }
};
// Pages without a sidebar item
const PAGE_ICONS = { '': LayoutDashboard, v2: LayoutDashboard, settings: Settings, account: UserRound, help: CircleHelp };
const viewOf = t => VIEW[t.view] || VIEW.open;
// The tab that speaks for a person: the one in use, then on screen, then most active, then latest
const byFront = (a, b) =>
  viewOf(a).rank - viewOf(b).rank ||
  (ORDER[a.state] ?? 0) - (ORDER[b.state] ?? 0) ||
  (b.movedAt || '').localeCompare(a.movedAt || '');

const whereNow = () =>
  document.visibilityState === 'hidden' ? 'back' : document.hasFocus() ? 'front' : 'open';

/** front / open / back for this browser tab */
const useWhere = () => {
  const [where, setWhere] = useState(whereNow);
  useEffect(() => {
    const update = () => setWhere(whereNow());
    window.addEventListener('focus', update);
    window.addEventListener('blur', update);
    document.addEventListener('visibilitychange', update);
    return () => {
      window.removeEventListener('focus', update);
      window.removeEventListener('blur', update);
      document.removeEventListener('visibilitychange', update);
    };
  }, []);
  return where;
};

// Kept in this browser tab across a refresh
const fromSession = (key, make, keep = () => true) => {
  try {
    const saved = sessionStorage.getItem(key);
    if (saved && keep(saved)) return saved;
    const value = make();
    sessionStorage.setItem(key, value);
    return value;
  } catch {
    return make();
  }
};
// Online since: from today only, for a tab left open overnight
const onlineSince = () =>
  fromSession(
    'online-since',
    () => new Date().toISOString(),
    saved => new Date(saved).toDateString() === new Date().toDateString()
  );
const tabKey = () => fromSession('online-tab', () => Math.random().toString(36).slice(2, 10));

// ONLINE_TODAY_V1: each tab pings crm_online_ping (src/database/online/online_today.sql) so
// Active Today knows who was on earlier and when they were last active. A ping goes on a page
// change, on going active, then every minute while active or every 2 minutes while open.
const PING_ACTIVE_MS = 60 * 1000;
const PING_OPEN_MS = 2 * 60 * 1000;
const TODAY_FRESH_MS = 20 * 1000;
const agoText = (at, now) => {
  const m = minutesBetween(at, now);
  return m < 1 ? 'just now' : `${duration(m)} ago`;
};

/**
 * ONLINE_NOW_V1: everyone on the CRM right now, top left of the header (Supabase Realtime
 * presence on one shared channel). Every open tab shares who they are, active / idle, whether
 * it's the tab they're using or a background one, and the page it's on. Each person is listed
 * once with all their tabs; clicking a tab takes you to that page (the same record tab).
 */
const OnlineNow = ({ userProfile, sidebarItems }) => {
  const [people, setPeople] = useState([]);
  const [now, setNow] = useState(() => Date.now());
  const [recordPlace, setPlace] = useState(getRecordPlace);
  const location = useLocation();
  const navigate = useNavigate();
  const mine = useMyState();
  const where = useWhere();
  const channelRef = useRef(null);
  const meta = useRef(null);
  const me = userProfile?.id;
  const myTab = useRef(null);
  if (!myTab.current) myTab.current = tabKey();
  const [list, setList] = useState('now'); // which list the card shows: now / today
  const [today, setToday] = useState(null); // crm_online_today rows, null until loaded
  const todayAt = useRef(0);
  const lastPing = useRef({ at: 0 });
  const ping = useRef(null);
  // TEAM_PROFILE_LINK_V1: staff who see the dashboard's Team section get a link to each person's breakdown
  const canTeam = !!me && canSeeTeam(userProfile);

  const path = `${location.pathname}${location.search}`;
  const place = recordPlace || pageName(location.pathname, sidebarItems);

  useEffect(() => onRecordPlace(setPlace), []);

  ping.current = () => {
    if (!me) return;
    const active = mine.state === 'active' && where !== 'back';
    const last = lastPing.current;
    const age = Date.now() - last.at;
    const due =
      last.path !== path ||
      last.place !== place ||
      (active && !last.active) ||
      age >= (active ? PING_ACTIVE_MS : PING_OPEN_MS);
    if (!due) return;
    lastPing.current = { at: Date.now(), path, place, active };
    supabase
      .rpc('crm_online_ping', { p_active: active, p_place: place || null, p_path: path })
      .then(() => {}, () => {});
  };
  useEffect(() => {
    ping.current();
  }, [me, path, place, mine.state, where]);
  useEffect(() => {
    const timer = setInterval(() => ping.current(), 30 * 1000);
    return () => clearInterval(timer);
  }, []);

  const loadToday = () => {
    if (!me || Date.now() - todayAt.current < TODAY_FRESH_MS) return;
    todayAt.current = Date.now();
    supabase.rpc('crm_online_today').then(
      ({ data, error }) => {
        if (!error && Array.isArray(data)) setToday(data);
      },
      () => {}
    );
  };
  useEffect(loadToday, [me]);

  useEffect(() => {
    if (!me) return undefined;
    meta.current = {
      id: me,
      tab: myTab.current,
      firstName: userProfile.firstName || '',
      lastName: userProfile.lastName || '',
      photo: userProfile.profilePhoto || null,
      since: onlineSince(),
      state: mine.state,
      stateSince: mine.at,
      view: where,
      path,
      place,
      movedAt: new Date().toISOString()
    };

    const channel = supabase.channel(CHANNEL, {
      config: { presence: { key: String(me) } }
    });
    channelRef.current = channel;

    const sync = () => {
      const list = Object.values(channel.presenceState())
        .filter(metas => metas.length)
        .map(metas => {
          // One entry per browser tab (a refresh can briefly leave the old one behind)
          const tabs = Object.values(
            metas.reduce((byTab, m) => {
              const k = m.tab || m.presence_ref;
              if (!byTab[k] || (m.movedAt || '') > (byTab[k].movedAt || '')) byTab[k] = m;
              return byTab;
            }, {})
          ).sort(byFront);
          return {
            ...tabs[0],
            since: metas.map(m => m.since).sort()[0],
            tabs
          };
        });
      setPeople(list);
    };

    channel.on('presence', { event: 'sync' }, sync).subscribe(status => {
      if (status === 'SUBSCRIBED') channel.track(meta.current);
    });

    return () => {
      channelRef.current = null;
      supabase.removeChannel(channel);
    };
    // Profile details only matter when we first join
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  // Share state, focus and page changes
  useEffect(() => {
    if (!meta.current) return;
    const moved = meta.current.path !== path;
    meta.current = {
      ...meta.current,
      state: mine.state,
      stateSince: mine.at,
      view: where,
      path,
      place,
      movedAt: moved ? new Date().toISOString() : meta.current.movedAt
    };
    if (channelRef.current?.state === 'joined') channelRef.current.track(meta.current);
  }, [mine.state, mine.at, where, path, place]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);

  if (!people.length) return null;

  const isMe = v => String(v.id) === String(me);
  const isThisTab = t => isMe(t) && t.tab === myTab.current;
  // For you, this tab speaks for you (what you're looking at now), not another of your tabs
  const withMine = v => {
    if (!isMe(v)) return v;
    const here = v.tabs.find(isThisTab);
    return here ? { ...v, ...here, since: v.since, tabs: [here, ...v.tabs.filter(t => t !== here)] } : v;
  };
  const byState = (a, b) => (ORDER[a.state] ?? 0) - (ORDER[b.state] ?? 0);
  const others = people
    .filter(v => !isMe(v))
    .sort((a, b) => byState(a, b) || (a.since || '').localeCompare(b.since || ''));
  const listed = [...people.filter(isMe).map(withMine), ...others];
  const shown = listed.slice(0, MAX_SHOWN);
  const extra = listed.length - MAX_SHOWN;
  const activeCount = listed.filter(v => (v.state || 'active') === 'active').length;
  const tabCount = listed.reduce((n, v) => n + v.tabs.length, 0);
  // A tab somewhere other than where you are: clicking it takes you there
  const goTo = t => (!isThisTab(t) && t.path && t.path !== path ? () => navigate(t.path) : null);
  const name = v => `${v.firstName} ${v.lastName}`.trim() || 'Someone';
  // The page's icon, the same one as in the sidebar
  const iconFor = p => {
    const first = (p || '').split(/[/?]/).filter(Boolean)[0] || '';
    return sidebarItems?.find(i => i.path === `/${first}`)?.icon || PAGE_ICONS[first] || AppWindow;
  };
  const PageIcon = ({ path: p, className }) => {
    const Icon = iconFor(p);
    return (
      <span className={`flex h-5 w-5 flex-none items-center justify-center rounded-md ring-1 ring-inset ${className}`}>
        <Icon size={12} strokeWidth={2} className='rounded-sm' />
      </span>
    );
  };

  // Active Today: on the CRM earlier today but not here now, last active first
  const here = new Set(people.map(v => String(v.id)));
  const earlier = (today || []).filter(v => !here.has(String(v.id)));
  const lastActive = v => v.lastActive || v.lastSeen;
  const shownList = today ? list : 'now';

  const TeamLink = ({ v }) =>
    canTeam ? (
      <button
        type='button'
        data-online-team={v.id}
        onClick={e => {
          e.stopPropagation();
          navigate('/', { state: { teamProfile: { id: v.id, name: name(v) } } });
        }}
        title={`${name(v)}'s Team Profile: today's stats and activity`}
        className='flex flex-none items-center gap-1 rounded-lg bg-white px-1.5 py-1 text-[11px] font-medium text-base-600 ring-1 ring-inset ring-base-900/[0.07] shadow-sm shadow-base-900/[0.03] transition-colors hover:text-brand-700 hover:ring-brand-600/25'
      >
        <ChartColumn size={12} strokeWidth={2} className='flex-none' />
        Team Profile
      </button>
    ) : null;

  // Same switch as Assisted / Portal / Guide / All in the top bar, with each list's count
  const listLabel = (text, count) => (
    <span className='flex items-center gap-1.5'>
      {text}
      <span className='font-medium tabular-nums text-base-400'>{count}</span>
    </span>
  );

  const Arrow = () => (
    <ArrowRight
      size={14}
      strokeWidth={2}
      className='flex-none text-base-300 transition-all group-hover/go:translate-x-0.5 group-hover/go:text-base-600'
    />
  );

  return (
    <div data-online-now onMouseEnter={loadToday} className='relative group flex items-center cursor-default'>
      <div className='flex items-center gap-2 rounded-full py-0.5 pl-0.5 pr-2.5 transition-colors group-hover:bg-base-100'>
        <div className='flex -space-x-1.5'>
          {shown.map(v => {
            const go = goTo(v);
            return (
              <div
                key={v.id}
                data-online-person={v.id}
                onClick={go || undefined}
                title={go ? `${name(v)}: go to ${v.place || 'where they are'}` : name(v)}
                className={`relative h-6 w-6 leading-none ${go ? 'cursor-pointer' : ''}`}
              >
                <UserProfilePicture
                  firstName={v.firstName}
                  lastName={v.lastName}
                  profilePicture={v.photo}
                  size='md'
                  className={`ring-2 ring-white rounded-full ${STATES[v.state]?.dim ? 'opacity-50 grayscale' : ''}`}
                />
                <span
                  className={`absolute bottom-0 right-0 h-2 w-2 rounded-full ring-[1.5px] ring-white ${(STATES[v.state] || STATES.active).dot}`}
                />
              </div>
            );
          })}
          {extra > 0 && (
            <div className='h-6 w-6 rounded-full bg-base-100 ring-2 ring-white flex items-center justify-center text-[11px] font-medium text-base-600'>
              +{extra}
            </div>
          )}
        </div>
        <span className='text-xs text-base-500 whitespace-nowrap'>
          {activeCount} Active Now
        </span>
      </div>

      {/* Hover card: fades and drops in under the avatars */}
      <div className='absolute left-0 top-full z-50 pt-2.5 opacity-0 invisible translate-y-1 transition-all duration-150 ease-out group-hover:opacity-100 group-hover:visible group-hover:translate-y-0'>
        <div className={`relative ${canTeam ? 'w-[25rem]' : 'w-[22rem]'} rounded-2xl bg-white ring-1 ring-base-900/5 shadow-xl shadow-base-900/10`}>
          <span className='absolute -top-1.5 left-3 h-3 w-3 rotate-45 bg-white ring-1 ring-base-900/5 [clip-path:polygon(0_0,100%_0,0_100%)]' />
          <div className='flex items-center justify-between gap-2 px-3 pt-3 pb-2'>
            {today ? (
              <SegmentedSwitch
                ariaLabel='Show'
                value={shownList}
                onChange={setList}
                options={[
                  { value: 'now', label: listLabel('Active Now', listed.length) },
                  { value: 'today', label: listLabel('Active Today', earlier.length) }
                ]}
              />
            ) : (
              <div className='flex items-center gap-2 pl-1'>
                <span className='relative flex h-2 w-2'>
                  <span className='absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-60 animate-ping' />
                  <span className='relative inline-flex h-2 w-2 rounded-full bg-green-500' />
                </span>
                <span className='text-[13px] font-semibold text-base-900'>Active Now</span>
              </div>
            )}
            <span className='flex-none rounded-full bg-base-100 px-2 py-0.5 text-[11px] font-medium text-base-600'>
              {shownList === 'today'
                ? 'Earlier today'
                : listed.length === 1 && tabCount === 1
                  ? 'Just you'
                  : `${listed.length} ${listed.length === 1 ? 'person' : 'people'} · ${tabCount} ${tabCount === 1 ? 'tab' : 'tabs'}`}
            </span>
          </div>

          {/* Active Today: on earlier, not here now */}
          {shownList === 'today' && (
            <ul data-online-today className='max-h-[65vh] overflow-y-auto px-2 pb-2'>
              {!earlier.length && (
                <li className='px-2 pb-2 pt-1 text-[12px] leading-relaxed text-base-500'>
                  Everyone who has been on the CRM today is still here.
                </li>
              )}
              {earlier.map(v => (
                <li key={v.id} data-online-earlier={v.id} className='rounded-xl px-2 py-2'>
                  <div className='flex items-center gap-3'>
                    <div className='relative flex-none h-10 w-10 leading-none'>
                      <UserProfilePicture
                        firstName={v.firstName}
                        lastName={v.lastName}
                        profilePicture={v.photo}
                        size='xl'
                        className='rounded-full ring-1 ring-base-200 opacity-60 grayscale'
                      />
                      <span className='absolute bottom-0 right-0 h-3 w-3 rounded-full ring-2 ring-white bg-base-300' />
                    </div>
                    <div className='flex min-w-0 flex-1 flex-col'>
                      <span className='truncate text-[13px] font-medium text-base-900'>{name(v)}</span>
                      <span className='mt-0.5 flex items-center gap-1 text-[11px] text-base-600 min-w-0'>
                        <History size={11} strokeWidth={2} className='flex-none text-base-400' />
                        <span className='truncate'>
                          Last active {timeOfDay(lastActive(v))}
                          <span className='text-base-300'> · </span>
                          {agoText(lastActive(v), now)}
                        </span>
                      </span>
                      {v.place && (
                        <span className='mt-1 flex items-center gap-1.5 text-[11px] text-base-500 min-w-0'>
                          <PageIcon path={v.path} className='!h-4 !w-4 bg-base-50 text-base-400 ring-base-900/5' />
                          <span className='truncate'>Last on {v.place}</span>
                        </span>
                      )}
                      <span className='mt-0.5 flex items-center gap-1 whitespace-nowrap text-[11px] text-base-400'>
                        <Clock size={11} strokeWidth={2} className='flex-none' />
                        First seen {timeOfDay(v.firstSeen)}
                      </span>
                    </div>
                    <TeamLink v={v} />
                  </div>
                </li>
              ))}
            </ul>
          )}

          <ul className={`max-h-[65vh] overflow-y-auto px-2 pb-2 ${shownList === 'now' ? '' : 'hidden'}`}>
            {listed.map(v => {
              const state = v.state || 'active';
              const look = STATES[state] || STATES.active;
              const inState = v.stateSince ? minutesBetween(v.stateSince, now) : 0;
              const single = v.tabs.length === 1;
              const go = single ? goTo(v) : null;
              return (
                <li
                  key={v.id}
                  data-online-row={v.id}
                  onClick={go || undefined}
                  title={go ? `Go to ${v.place || 'where they are'}` : undefined}
                  className={`group/go rounded-xl px-2 py-2 ${single ? 'hover:bg-base-50' : ''} ${go ? 'cursor-pointer' : ''}`}
                >
                  <div className='flex items-center gap-3'>
                    <div className='relative flex-none h-10 w-10 leading-none'>
                      <UserProfilePicture
                        firstName={v.firstName}
                        lastName={v.lastName}
                        profilePicture={v.photo}
                        size='xl'
                        className={`rounded-full ring-1 ring-base-200 ${look.dim ? 'opacity-50 grayscale' : ''}`}
                      />
                      <span className={`absolute bottom-0 right-0 h-3 w-3 rounded-full ring-2 ring-white ${look.dot}`} />
                    </div>
                    <div className='flex min-w-0 flex-1 flex-col'>
                      <div className='flex items-center gap-1.5 min-w-0'>
                        <span className='truncate text-[13px] font-medium text-base-900'>{name(v)}</span>
                        {isMe(v) && (
                          <span className='flex-none rounded-full bg-brand-50 px-1.5 py-px text-[10px] font-medium text-brand-700'>
                            You
                          </span>
                        )}
                        {!single && (
                          <span
                            data-online-tabcount
                            className='flex-none rounded-full bg-base-100 px-1.5 py-px text-[10px] font-medium text-base-600'
                          >
                            {v.tabs.length} Tabs
                          </span>
                        )}
                      </div>
                      <span className='mt-0.5 flex items-center gap-1 text-[11px] text-base-600 min-w-0'>
                        <look.Icon size={11} strokeWidth={2} className='flex-none text-base-400' />
                        <span className='truncate'>
                          {look.text}
                          {state !== 'active' && inState >= 1 && ` for ${duration(inState)}`}
                        </span>
                      </span>
                      {single && v.place && (
                        <span className='mt-1 flex items-center gap-1.5 text-[11px] text-base-700 min-w-0'>
                          <PageIcon path={v.path} className='!h-4 !w-4 bg-base-50 text-base-500 ring-base-900/5' />
                          <span className='truncate'>{v.place}</span>
                        </span>
                      )}
                      <span className='mt-0.5 flex items-center gap-1 whitespace-nowrap text-[11px] text-base-400'>
                        <Clock size={11} strokeWidth={2} className='flex-none' />
                        Online since {timeOfDay(v.since)}
                        <span className='text-base-300'>·</span>
                        {duration(minutesBetween(v.since, now))}
                      </span>
                    </div>
                    <TeamLink v={v} />
                    {go && <Arrow />}
                  </div>

                  {/* Several tabs: each one, the one in use first, each a link to its page */}
                  {!single && (
                    <ul className='mt-2 mb-0 ml-[3.25rem] space-y-px rounded-xl bg-base-50/60 p-1 ring-1 ring-inset ring-base-900/[0.04]'>
                      {v.tabs.map(t => {
                        const view = viewOf(t);
                        const goTab = goTo(t);
                        const here = isThisTab(t);
                        return (
                          <li
                            key={t.tab || t.presence_ref}
                            data-online-tab={t.view}
                            onClick={goTab || undefined}
                            title={goTab ? `Go to ${t.place || 'this tab'}` : undefined}
                            className={`group/go flex items-center gap-2.5 rounded-lg px-1.5 py-1 transition-colors ${goTab ? 'cursor-pointer hover:bg-base-50' : ''}`}
                          >
                            <PageIcon path={t.path} className={view.icon} />
                            <span className={`min-w-0 flex-1 truncate text-[12px] ${view.text}`}>
                              {t.place || 'CRM'}
                            </span>
                            {here ? (
                              <span className='flex-none rounded-full bg-brand-50 px-1.5 py-px text-[10px] font-medium text-brand-700'>
                                This Tab
                              </span>
                            ) : (
                              <span className={`flex flex-none items-center gap-1 text-[10.5px] ${view.status}`}>
                                <span className={`h-1.5 w-1.5 rounded-full ${view.dot}`} />
                                {view.label}
                              </span>
                            )}
                            {goTab ? <Arrow /> : <span className='w-3.5 flex-none' />}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default OnlineNow;
